import { BiPlus, BiX } from "react-icons/bi"
import { useState, useEffect } from "react"

// Types regroupés
const FIELD_TYPES = {
    TEXT: ["text", "email", "password", "url", "tel"],
    TEXTAREA: ["textarea"],
    NUMBER: ["number", "integer"],
    DOUBLE: ["double", "decimal", "float"],
    SELECT: ["select"],
    CHECKBOX: ["checkbox"],
    RADIO: ["radio"],
    RANGE: ["range"],
    DATE: ["date"],
    TIME: ["time"],
    DATETIME: ["datetime", "datetime-local"],
    COLOR: ["color"],
    FILE: ["file"],
}

export default function DataInputForm({ visibleChamps, dataInput, setDataInput, dataEdit, setDataEdit, loading }) {
    const generateCode = () => Date.now().toString()

    const [formData, setFormData] = useState({})
    const [formErrors, setFormErrors] = useState({})
    const [isEditMode, setIsEditMode] = useState(false)

    useEffect(() => {
        if (dataEdit) {
            setFormData(dataEdit)
            setIsEditMode(true)
        }
    }, [dataEdit])

    const handleAddData = () => {
        const dataErrors = validateForm(formData)
        if (Object.keys(dataErrors).length > 0) {
            setFormErrors(dataErrors)
            return
        }
        const dataToAdd = { ...formData, key_data: generateCode() }

        setDataInput([...dataInput, dataToAdd])
        setFormData({})
        setFormErrors({})
        setDataEdit(null)
        setIsEditMode(false)
    }

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target
        const newValue = type === "checkbox" ? checked : value

        setFormData((prev) => ({
            ...prev,
            [name]: newValue,
        }))

        if (formErrors[name] && newValue) {
            setFormErrors((prev) => {
                const newErrors = { ...prev }
                delete newErrors[name]
                return newErrors
            })
        }
    }

    const validateForm = (formData) => {
        const newErrors = {}
        visibleChamps.forEach((c) => {
            const champRequis = c.requis?.toLowerCase() === "oui"
            if (champRequis && (!formData[c.nom_ligne] || String(formData[c.nom_ligne]).trim() === "")) {
                newErrors[c.nom_ligne] = `Le champ "${c.nom_ligne}" est obligatoire.`
            }
        })
        return newErrors
    }

    const renderField = (champ) => {
        const type = champ.type_ligne?.toLowerCase() || "text"
        const value = formData[champ.nom_ligne] || ""
        const hasError = !!formErrors[champ.nom_ligne]

        // TEXTAREA
        if (FIELD_TYPES.TEXTAREA.includes(type)) {
            return (
                <textarea
                    id={champ.nom_ligne}
                    name={champ.nom_ligne}
                    rows={champ.rows || 3}
                    className={`form-control form-control-sm ${hasError ? "is-invalid" : ""}`}
                    value={value}
                    onChange={handleChange}
                />
            )
        }

        // SELECT
        if (FIELD_TYPES.SELECT.includes(type)) {
            return (
                <select
                    id={champ.nom_ligne}
                    name={champ.nom_ligne}
                    className={`form-select form-select-sm ${hasError ? "is-invalid" : ""}`}
                    value={value}
                    onChange={handleChange}
                >
                    <option value="">Sélectionner...</option>
                    {champ.options_ligne?.split(",").map((opt, idx) => (
                        <option key={idx} value={opt.trim()}>{opt.trim()}</option>
                    ))}
                </select>
            )
        }

        // CHECKBOX
        if (FIELD_TYPES.CHECKBOX.includes(type)) {
            return (
                <div className="form-check form-switch">
                    <input
                        type="checkbox"
                        id={champ.nom_ligne}
                        name={champ.nom_ligne}
                        className={`form-check-input ${hasError ? "is-invalid" : ""}`}
                        checked={formData[champ.nom_ligne] || false}
                        onChange={handleChange}
                    />
                    {champ.libelle_ligne && (
                        <label className="form-check-label" htmlFor={champ.nom_ligne}>
                            {champ.libelle_ligne}
                        </label>
                    )}
                </div>
            )
        }

        // RADIO
        if (FIELD_TYPES.RADIO.includes(type)) {
            return (
                <div>
                    {champ.options_ligne?.split(",").map((opt, idx) => (
                        <div className="form-check" key={opt.trim()}>
                            <input
                                type="radio"
                                id={`${champ.nom_ligne}_${idx}`}
                                name={champ.nom_ligne}
                                value={opt.trim()}
                                className={`form-check-input ${hasError ? "is-invalid" : ""}`}
                                checked={value === opt.trim()}
                                onChange={handleChange}
                            />
                            <label className="form-check-label" htmlFor={`${champ.nom_ligne}_${idx}`}>
                                {opt.trim()}
                            </label>
                        </div>
                    ))}
                </div>
            )
        }

        // FILE
        if (FIELD_TYPES.FILE.includes(type)) {
            return (
                <input
                    type="file"
                    id={champ.nom_ligne}
                    name={champ.nom_ligne}
                    className={`form-control form-control-sm ${hasError ? "is-invalid" : ""}`}
                    onChange={handleChange}
                />
            )
        }

        // NUMBER (entier)
        if (FIELD_TYPES.NUMBER.includes(type)) {
            return (
                <input
                    type="number"
                    id={champ.nom_ligne}
                    name={champ.nom_ligne}
                    className={`form-control form-control-sm ${hasError ? "is-invalid" : ""}`}
                    value={value}
                    onChange={handleChange}
                />
            )
        }

        // DOUBLE (décimal)
        if (FIELD_TYPES.DOUBLE.includes(type)) {
            return (
                <input
                    type="number"
                    step="0.01"
                    id={champ.nom_ligne}
                    name={champ.nom_ligne}
                    className={`form-control form-control-sm ${hasError ? "is-invalid" : ""}`}
                    value={value}
                    onChange={handleChange}
                />
            )
        }

        // Autres types natifs (date, time, datetime, color, range, text…)
        return (
            <input
                type={type}
                id={champ.nom_ligne}
                name={champ.nom_ligne}
                className={`form-control form-control-sm ${hasError ? "is-invalid" : ""}`}
                value={value}
                onChange={handleChange}
            />
        )
    }

    return (
        <div>
            <div className="row g-2">
                {visibleChamps
                .slice().sort((a, b) => a.rang - b.rang)
                .map((champ) => (
                    <div className="col-12" key={champ.code_feuille_ligne}>
                        <label htmlFor={champ.nom_ligne} className="form-label fw-semibold">
                            {champ.libelle_ligne} {champ.requis?.toLowerCase() === "oui" && <span className="text-danger">*</span>}
                        </label>

                        {/* Configuration du champ */}
                        {renderField(champ)}

                        {formErrors[champ.nom_ligne] && (
                            <div className="invalid-feedback">{formErrors[champ.nom_ligne]}</div>
                        )}
                    </div>
                ))}
            </div>

            <div className="d-flex justify-content-between gap-2 mt-4">
                <button type="button" onClick={handleAddData} className="btn btn-success btn-sm d-flex align-items-center" disabled={loading}>
                    <BiPlus className="me-1" />
                    {isEditMode ? "Modifier" : "Ajouter"}
                </button>

                <button
                    type="button"
                    className="btn btn-sm btn-light px-2 d-flex align-items-center"
                    onClick={() => setFormData({})}
                    disabled={loading}>
                    <BiX className="me-1" />
                    Réinitialiser
                </button>
            </div>
        </div>
    )
}
